import lodashOmit from 'lodash/omit';
import {createSelector} from 'reselect';
import {COLLECTION_TYPES} from '../../constants/issue-query-params';
import lodashIsEqual from 'lodash/isEqual';
import {preloadedFilterQueryParamsState} from '../../constants/preloaded-states';
import {viewTypes} from '../../constants/view-types';
import {TEMPLATE_CATEGORY_FIELD_NAMES, TEMPLATE_FIELD_NAMES} from '../../constants/issue-templates';
import getInitialValuesFromTemplate from './getInitialValuesFromTemplate';
import {getCurrentUserId} from '../selectors';
import {selectors as categoriesInProgressSelectors} from './template-categories-in-progress-reducer';

export const getCurrentPreviewTemplateId = state => state.currentPreviewTemplateId;
export const getCurrentDuplicateTemplateId = state => state.currentDuplicateTemplateId;
export const getDeleteTemplateId = state => state.templateDelete.templateId;
export const isShowNewTemplateModal = state => state.showNewTemplateModal;
export const getPendingTemplate = state => state.pendingTemplate;
export const getPendingTemplateForm = state => state.pendingTemplateForm.$form;
export const getPendingTemplateFormAttributes = state => lodashOmit(state.pendingTemplateForm, ['$form']);

const getTemplateCollection = state => state.templateCollection;
const getTemplateCategoryCollection = state => state.templateCategoryCollection;
export const getTemplatesQueryParams = state => state.queryParams[COLLECTION_TYPES.TEMPLATES];

export const isTemplatesCollectionLoading = createSelector(
  getTemplateCollection,
  ({loading}) => loading
);

export const isTemplatesCollectionError = createSelector(
  getTemplateCollection,
  ({error}) => error
);

export const isTemplatesCollectionDidFetchOnce = createSelector(
  getTemplateCollection,
  ({didFetchOnce}) => didFetchOnce
);

export const getTemplateEntitiesMap = createSelector(
  getTemplateCollection,
  ({entitiesMap}) => entitiesMap
);

export const getTemplateOrderedIds = createSelector(
  getTemplateCollection,
  ({orderedIds}) => orderedIds
);

export const getTemplatesSearchResults = createSelector(
  getTemplateCollection,
  ({searchResults}) => searchResults
);

export const getTemplateOrderedList = createSelector(
  getTemplateEntitiesMap,
  getTemplateOrderedIds,
  (entitiesMap, orderedIds) => orderedIds.map(id => entitiesMap[id])
);

export const getTemplatesCollectionPagination = createSelector(
  getTemplateCollection,
  ({pagination}) => pagination
);

export const getTemplatesCollectionHighlightRowIds = createSelector(
  getTemplateCollection,
  ({highlightRowIds}) => highlightRowIds
);

export const isTemplateCategoriesCollectionLoading = createSelector(
  getTemplateCategoryCollection,
  ({loading}) => loading
);

export const getTemplateCategoriesEntitiesMap = createSelector(
  getTemplateCategoryCollection,
  ({entitiesMap}) => entitiesMap
);

export const getTemplateCategoriesOrderedIds = createSelector(
  getTemplateCategoryCollection,
  ({orderedIds}) => orderedIds
);

export const getUncategorizedTemplateCategoryId = createSelector(
  getTemplateCategoryCollection,
  ({uncategorizedId}) => uncategorizedId
);

export const getUncategorizedTemplateCategory = createSelector(
  getTemplateCategoriesEntitiesMap,
  getUncategorizedTemplateCategoryId,
  (entitiesMap, uncategorizedId) => entitiesMap[uncategorizedId]
);

export const getTemplateCategories = createSelector(
  getTemplateCategoriesEntitiesMap,
  getTemplateCategoriesOrderedIds,
  (entitiesMap, orderedIds) => orderedIds.map(id => entitiesMap[id])
);

export const getIsTemplateCategoriesManageModalOpen = state => state.isManageTemplateCategoriesModalOpen;

export const isTemplatesFilterChanged = createSelector(
  getTemplatesQueryParams,
  ({sort, search, offset, ...queryParams}) =>
    !lodashIsEqual(preloadedFilterQueryParamsState[viewTypes.PROJECT][COLLECTION_TYPES.TEMPLATES], queryParams)
);

export const isTemplatesSearchChanged = createSelector(
  getTemplatesQueryParams,
  ({search}) => !!search
);

export const getActiveTemplatesEntitiesMap = state => state.activeTemplates.entitiesMap;
export const getActiveTemplatesOrderedIds = state => state.activeTemplates.orderedIds;

export const getActiveTemplates = createSelector(
  getActiveTemplatesEntitiesMap,
  getTemplateCategoriesEntitiesMap,
  getActiveTemplatesOrderedIds,
  (entitiesMap, templateCategoriesEntitiesMap, orderedIds) =>
    orderedIds
      .map(id => entitiesMap[id])
      .filter(template => {
        const templateCategory = templateCategoriesEntitiesMap[template[TEMPLATE_FIELD_NAMES.CATEGORY_ID]];
        return !templateCategory || templateCategory.isActive;
      })
);

export const getRecentlyUsedTemplateIds = state => state.activeTemplates.recentlyUsed;

export const getRecentlyUsedTemplates = createSelector(
  getRecentlyUsedTemplateIds,
  getActiveTemplatesEntitiesMap,
  (templateIds, entitiesMap) => templateIds.map(id => entitiesMap[id]).filter(Boolean)
);

export const getFormValuesFromTemplate = createSelector(
  getActiveTemplatesEntitiesMap,
  getCurrentUserId,
  (state, templateId) => templateId,
  (activeTemplatesEntitiesMap, currentUserId, templateId) =>
    getInitialValuesFromTemplate(activeTemplatesEntitiesMap[templateId], currentUserId)
);

export const getIsFetchingActiveTemplates = state => state.activeTemplates.pending;

export const getIsTemplateCategoryCreateInProgress = state => state.newTemplateCategory.loading;

const getTemplateCategoriesIncludingUncategorized = createSelector(
  getTemplateCategories,
  getUncategorizedTemplateCategory,
  (templateCategories, uncategorizedTemplateCategory) =>
    uncategorizedTemplateCategory ? [...templateCategories, uncategorizedTemplateCategory] : templateCategories
);

export const getTemplateCategoryTitlesForUniqueValidation = createSelector(
  getTemplateCategoriesIncludingUncategorized,
  templateCategories => templateCategories.map(x => x[TEMPLATE_CATEGORY_FIELD_NAMES.TITLE].toLowerCase())
);

const getTemplateCategoriesInProgress = state => state.templateCategoriesInProgress;

export const getTemplateCategoriesInUpdateProgressMap = createSelector(
  getTemplateCategoriesInProgress,
  categoriesInProgressSelectors.getCategoriesInUpdateProgressMap
);

export const getTemplateCategoriesInDeleteProgressMap = createSelector(
  getTemplateCategoriesInProgress,
  categoriesInProgressSelectors.getCategoriesInDeleteProgressMap
);

export const getTemplateCategoriesInFetchTemplatesProgress = createSelector(
  getTemplateCategoriesInProgress,
  categoriesInProgressSelectors.getCategoriesInFetchTemplatesProgressMap
);
